import React from "react";
import Popup from "@/components/ui/popup";
import useTranslation from "next-translate/useTranslation";
import Script from "next/script";
import { useRouter } from "next/router";
import { generateId } from "@/util/generateId";
import FBScript from "@/components/util/fb-script";
import { useRegisterForm } from "./useRegisterForm";
import { FormStep, UsernameStep, JoinedStep } from "./RegisterSteps";

export default function RegisterPopup({
  title,
  subtitle = "",
  onClose,
  redirectAfterDone,
  guestLink,
}: {
  title?: string;
  subtitle?: string;
  onClose: () => void;
  redirectAfterDone?: () => void;
  guestLink?: React.ReactNode;
}) {
  const { t } = useTranslation("common");
  const { t: registerT } = useTranslation("auth");
  const router = useRouter();

  const {
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    usernameError,
    emailError,
    passwordError,
    emailStyle,
    passwordStyle,
    usernameStyle,
    guestElo,
    setNews,
    emailChange,
    emailBlur,
    usernameChange,
    passwordChange,
    advanceToUsernameStep,
    validateCreateAccount,
    createSocialAccount,
    signInFBInline,
    signInAppleWebInline,
    initializeGoogleButton,
  } = useRegisterForm({
    onExistingUserSignIn: () => {
      if (redirectAfterDone) {
        redirectAfterDone();
        onClose();
      } else if (router.pathname === "/register") {
        router.push("/play");
      } else {
        onClose();
      }
    },
  });

  return (
    <>
      <Popup
        subtitle={
          step === "socialUsername" || step === "username"
            ? registerT("Your username will be visible to other users")
            : step === "form"
            ? subtitle //registerT("Continue with your favorite account")
            : null
        }
        title={
          step === "joined"
            ? ""
            : step === "socialUsername"
            ? registerT("Choose your username")
            : step === "form"
            ? title
              ? title
              : t("Create free account")
            : t("Create free account")
        }
        onClose={onClose}
        maxWidth="464px"
      >
        <FBScript />
        <Script
          onLoad={() => {
            //@ts-ignore
            window.AppleID?.auth?.init?.({
              clientId: "com.chesshotel.service",
              scope: "",
              redirectURI:
                "https://www.chesshotel.com/api/v1/auth/apple/redirect",
              state: "Eksätravägen975031",
              nonce: generateId(),
              usePopup: true,
            });
          }}
          src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        />
        <Script
          src={`https://accounts.google.com/gsi/client?hl=${router.locale}`}
          strategy="afterInteractive"
          onLoad={() => {
            console.log("Google Identity Services script loaded");
            initializeGoogleButton();
          }}
        />
        {step === "joined" ? (
          <JoinedStep
            t={t}
            registerT={registerT}
            signedUpWithEmail={signedUpWithEmail}
            onClose={onClose}
            router={router}
            redirectAfterRegistration={redirectAfterDone}
          />
        ) : step === "username" ? (
          <UsernameStep
            t={t}
            registerT={registerT}
            username={username}
            usernameStyle={usernameStyle}
            usernameError={usernameError}
            onUsernameChange={usernameChange}
            onSubmit={validateCreateAccount}
          />
        ) : step === "socialUsername" ? (
          <UsernameStep
            t={t}
            registerT={registerT}
            username={username}
            usernameStyle={usernameStyle}
            usernameError={usernameError}
            onUsernameChange={usernameChange}
            onSubmit={createSocialAccount}
          />
        ) : (
          <FormStep
            t={t}
            registerT={registerT}
            guestElo={guestElo}
            email={email}
            password={password}
            emailError={emailError}
            passwordError={passwordError}
            emailStyle={emailStyle}
            passwordStyle={passwordStyle}
            onEmailChange={emailChange}
            onEmailBlur={emailBlur}
            onPasswordChange={passwordChange}
            onSetNews={setNews}
            onSubmit={advanceToUsernameStep}
            onSignInFB={signInFBInline}
            onSignInApple={signInAppleWebInline}
            guestLink={guestLink}
          />
        )}
      </Popup>
    </>
  );
}
